// The marker menu (R1294, player-reach.md §2.2, ui-map-interface.md §8).
//
// An alt+click on the map, or MARK on a pinned peek, opens the menu at the
// pressed point: four targets at N, E, S and W for the four kinds of a map
// marker, and a toggle in the middle for the audience. A press on a kind
// sends `MarkerAction { kind, cell, audience }` and closes the menu. A tap
// on the toggle flips SQUAD and FACTION, and the settings keep the choice.
// A press outside the menu, or Escape, closes it and does nothing else.
//
// **It is DOM** (the canvas/DOM split of §1): the targets are buttons with
// words on them, so the menu is chrome over the canvas and never a layer.
// Each target paints its kind on a small canvas with the menu picture of
// the active look (round 062 `drawMenu`), so a target and a marker on the
// map are one picture. Every control acts on the PRESS, because the menu
// is fixed chrome and no host scrolls under it (§16.8).
//
// **Figures.** The reach, the target and the toggle size are the
// `menu_*_px` figures of `chart.toml` `[chart.piece.map_markers]`, in
// chrome px, so the map zoom leaves the menu alone. The menu stands in
// `#ui`, so the UI scale scales it with the rest of the chrome, and `open`
// turns the pressed point into chrome px (R1556).

import { CHART, KEYS } from '../gen/presentation';
import { markers as S } from '../gen/strings';
import { matchKey } from '../keys';
import {
  menuLabelled, paintMenuTarget, type MarkerInks, type PaintCtx,
} from '../render/map-marker-cells';
import {
  MARKER_KIND_SLUGS, type MarkerAudienceSlug, type MarkerKindSlug,
} from '../render/map-markers';
import { isPrimaryPress, pressedControl } from './pressroute';
import type { MapMarkerStyle } from './settings';
import { chromeViewport, toChromePx } from './uiscale';
import { deviceRatio } from '../render/dpr';

/** The menu's own figures, in screen px. */
export type MenuFig = Pick<typeof CHART.pieces.map_markers.fig,
  'menu_reach_px' | 'menu_target_px' | 'menu_centre_px'>;

/** Where each kind stands round the pressed point: N, E, S and W. */
const DIRS: Record<MarkerKindSlug, readonly [number, number]> = {
  attack: [0, -1], defend: [1, 0], gather: [0, 1], danger: [-1, 0],
};

/** The gap the menu keeps from the edge of the window, in px. */
const EDGE_PX = 4;

/** Where the picture of a target stands over its centre, as the card set it. */
const PICTURE_LIFT = 3;

/** The four targets and the toggle, laid out round one point. */
export interface MenuLayout {
  /** The centre of the menu, after the clamp to the window. */
  cx: number;
  cy: number;
  targets: { kind: MarkerKindSlug; x: number; y: number }[];
  /** The radius of a target and of the toggle. */
  r: number;
  centreR: number;
}

/**
 * The layout round a pressed point. The whole menu stays inside a window
 * of `vw` × `vh`, so a press near the edge moves the menu in and never
 * cuts a target off.
 */
export function menuLayout(px: number, py: number, vw: number, vh: number, f: MenuFig): MenuLayout {
  const r = f.menu_target_px / 2;
  const span = f.menu_reach_px + r + EDGE_PX;
  const clamp = (v: number, size: number) => (size < 2 * span ? size / 2 : Math.min(size - span, Math.max(span, v)));
  const cx = clamp(px, vw);
  const cy = clamp(py, vh);
  return {
    cx, cy, r, centreR: f.menu_centre_px / 2,
    targets: MARKER_KIND_SLUGS.map((kind) => ({
      kind,
      x: cx + DIRS[kind][0] * f.menu_reach_px,
      y: cy + DIRS[kind][1] * f.menu_reach_px,
    })),
  };
}

/** The name of each kind on its target. */
const KIND_NAME: Record<MarkerKindSlug, string> = {
  attack: S.kind_attack, defend: S.kind_defend, gather: S.kind_gather, danger: S.kind_danger,
};

/** The pencil word of each kind, which card 05 paints as its target. */
const KIND_WORD: Record<MarkerKindSlug, string> = {
  attack: S.pencil_attack, defend: S.pencil_defend, gather: S.pencil_gather, danger: S.pencil_danger,
};

/** What the menu needs of the page. */
export interface MarkerMenuDeps {
  /** The `#ui` element the menu stands in. */
  host: HTMLElement;
  /** The look the targets paint in: `look.marks.mapMarker`. */
  look(): MapMarkerStyle;
  /** The audience the toggle shows, and its store (the settings blob). */
  audience(): MarkerAudienceSlug;
  setAudience(a: MarkerAudienceSlug): void;
  /** Sends the place intent. */
  place(kind: MarkerKindSlug, cell: number, audience: MarkerAudienceSlug): void;
  /** The four inks of the looks, and the Fell font stack. */
  inks: MarkerInks;
  fell: string;
  /** The figures. The shipped table without an answer. */
  fig?: MenuFig;
}

export class MarkerMenu {
  private el: HTMLElement | null = null;
  private toggleEl: HTMLButtonElement | null = null;
  private cell = -1;
  private layoutNow: MenuLayout | null = null;

  constructor(private readonly d: MarkerMenuDeps) {}

  /** Is the menu on the screen? */
  get isOpen(): boolean { return this.el !== null; }

  /** The layout of the open menu, or null. The tests read it. */
  get layout(): MenuLayout | null { return this.layoutNow; }

  /** The element of the open menu, or null. The tests read it. */
  get element(): HTMLElement | null { return this.el; }

  /**
   * Opens the menu round a screen point, for the cell under that point.
   * A menu that is open already closes first, so one press never leaves
   * two menus on the screen.
   */
  open(px: number, py: number, cell: number): void {
    this.close();
    const f = this.d.fig ?? CHART.pieces.map_markers.fig;
    const v = chromeViewport();
    const l = menuLayout(toChromePx(px), toChromePx(py), v.w, v.h, f);
    this.layoutNow = l;
    this.cell = cell;
    const look = this.d.look();
    const root = document.createElement('div');
    root.id = 'markermenu';
    root.setAttribute('role', 'menu');
    root.setAttribute('aria-label', S.menu_label);
    root.style.left = `${l.cx}px`;
    root.style.top = `${l.cy}px`;
    const reach = document.createElement('div');
    reach.className = 'reach';
    const d = 2 * f.menu_reach_px;
    reach.style.width = `${d}px`;
    reach.style.height = `${d}px`;
    root.appendChild(reach);
    for (const t of l.targets) {
      const b = document.createElement('button');
      b.className = 'kind';
      b.dataset.kind = t.kind;
      b.setAttribute('role', 'menuitem');
      b.setAttribute('aria-label', KIND_NAME[t.kind]);
      place(b, t.x - l.cx, t.y - l.cy, f.menu_target_px);
      b.appendChild(this.picture(look, t.kind, f.menu_target_px));
      if (menuLabelled(look)) {
        const name = document.createElement('span');
        name.className = 'name';
        name.textContent = KIND_NAME[t.kind];
        b.appendChild(name);
      }
      root.appendChild(b);
    }
    const tog = document.createElement('button');
    tog.className = 'aud';
    tog.title = S.audience_hint;
    place(tog, 0, 0, f.menu_centre_px);
    root.appendChild(tog);
    this.toggleEl = tog;
    this.writeToggle();
    root.addEventListener('pointerdown', this.onPress);
    // A keyboard reaches the buttons with Tab, and a native button turns
    // Enter and Space into a click with no pointer: `detail` is 0 there,
    // so a mouse press never acts twice.
    root.addEventListener('click', this.onKeyClick);
    this.d.host.appendChild(root);
    this.el = root;
    document.addEventListener('pointerdown', this.onOutside, true);
    window.addEventListener('keydown', this.onKey, true);
  }

  /** Takes the menu off the screen. */
  close(): void {
    if (!this.el) return;
    this.el.remove();
    this.el = null;
    this.toggleEl = null;
    this.layoutNow = null;
    document.removeEventListener('pointerdown', this.onOutside, true);
    window.removeEventListener('keydown', this.onKey, true);
  }

  /** Acts on one control of the menu, by its button. */
  private act(b: HTMLButtonElement): void {
    if (b.classList.contains('aud')) {
      this.d.setAudience(this.d.audience() === 'squad' ? 'faction' : 'squad');
      this.writeToggle();
      return;
    }
    const kind = b.dataset.kind as MarkerKindSlug | undefined;
    if (!kind || !MARKER_KIND_SLUGS.includes(kind)) return;
    const cell = this.cell;
    this.close();
    this.d.place(kind, cell, this.d.audience());
  }

  private readonly onPress = (ev: Event): void => {
    if (!isPrimaryPress(ev)) return;
    const b = pressedControl<HTMLButtonElement>(ev, 'button');
    ev.preventDefault();
    ev.stopPropagation();
    if (b) this.act(b);
  };

  private readonly onKeyClick = (ev: MouseEvent): void => {
    if (ev.detail !== 0) return;
    const b = pressedControl<HTMLButtonElement>(ev, 'button');
    if (b) this.act(b);
  };

  /** A press outside the menu closes it, and the press does nothing else. */
  private readonly onOutside = (ev: Event): void => {
    if (!this.el || this.el.contains(ev.target as Node)) return;
    ev.preventDefault();
    ev.stopPropagation();
    this.close();
  };

  /** Escape closes the menu before any other surface reads the key. */
  private readonly onKey = (ev: KeyboardEvent): void => {
    if (matchKey(ev, { escape: KEYS.map.escape }) !== 'escape') return;
    ev.preventDefault();
    ev.stopPropagation();
    this.close();
  };

  /** The toggle reads the audience a press on a kind sends. */
  private writeToggle(): void {
    const t = this.toggleEl;
    if (!t) return;
    const a = this.d.audience();
    t.textContent = a === 'squad' ? S.audience_squad : S.audience_faction;
    t.dataset.aud = a;
    t.setAttribute('aria-label', `${t.textContent} · ${S.audience_hint}`);
  }

  /** The picture of one target: the look's menu picture on a small canvas. */
  private picture(look: MapMarkerStyle, kind: MarkerKindSlug, size: number): HTMLCanvasElement {
    const cv = document.createElement('canvas');
    const dpr = deviceRatio();
    cv.width = Math.round(size * dpr);
    cv.height = Math.round(size * dpr);
    cv.style.width = `${size}px`;
    cv.style.height = `${size}px`;
    let c: CanvasRenderingContext2D | null = null;
    try { c = cv.getContext('2d'); } catch { c = null; }
    if (!c) return cv;
    c.scale(dpr, dpr);
    c.translate(size / 2, size / 2 - PICTURE_LIFT);
    paintMenuTarget(c as unknown as PaintCtx, look, kind, this.d.inks, KIND_WORD[kind], this.d.fell);
    return cv;
  }
}

/** Places a round button of `size` px with its centre at an offset. */
function place(b: HTMLElement, dx: number, dy: number, size: number): void {
  b.style.width = `${size}px`;
  b.style.height = `${size}px`;
  b.style.left = `${dx - size / 2}px`;
  b.style.top = `${dy - size / 2}px`;
}
